import React, { useState, useEffect } from "react";
import { ReportTypeFilter } from "../filters/reportTypeFilter";
import { ActivityFrequencyFilter } from "../filters/activityFrequencyFilter";
import { ReportingCustomerFilter } from "../filters/customerFilter";
import { ReportingSiteFilter } from "../filters/siteFilter";
import { ReportingUserFilter } from "../filters/userFilter";
import { ReportingActivityTypeFilter } from "../filters/activityTypeFilter";
import { Button } from "../../../components/button";

import Datepicker from "react-tailwindcss-datepicker";

function FilterPlanel({ onUpdate }) {
  const [reportType, setReportType] = useState(["activity"]);
  const [reportingFrequency, setReportingFrequency] = useState(["daily"]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);
  const [sitesOptions, setSitesOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  /// Handler for "run" click ///
  const handleRunClick = () => {
    const filters = {
      reportType: reportType[0],
      reportingFrequency: reportingFrequency[0],
      selectedCustomers,
      selectedSites,
      selectedActivityTypes,
      selectedUsers,
      dateRange,
    };
    onUpdate(filters);
  };

  /// Handler for report type change ///
  const handleReportTypeChange = (newReportType) => {
    setReportType(newReportType);
  };

  /// Handle Activity Frequency changes ///
  // This callback now receives an array (from MultiSelect)
  const handleFrequencyChange = (newFrequencyArray) => {
    setReportingFrequency(newFrequencyArray);
    const newFrequency = newFrequencyArray[0] || "daily";
    if (newFrequency === "daily") {
      // Make endDate = startDate
      setDateRange((prev) => ({
        startDate: prev.startDate,
        endDate: prev.startDate,
      }));
    } else if (newFrequency === "weekly") {
      // Make endDate = startDate + 7 days (or 6, as per your preference)
      setDateRange((prev) => {
        const start = new Date(prev.startDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 7);
        return {
          startDate: prev.startDate,
          endDate: end.toISOString().split("T")[0],
        };
      });
    } else if (newFrequency === "monthly") {
      // Snap to the calendar month containing the current start date.
      setDateRange((prev) => {
        const start = new Date(prev.startDate);
        const y = start.getUTCFullYear();
        const m = start.getUTCMonth();
        return {
          startDate: new Date(Date.UTC(y, m, 1)).toISOString().split("T")[0],
          endDate: new Date(Date.UTC(y, m + 1, 0)).toISOString().split("T")[0],
        };
      });
    }
  };

  /// Customers dropdown (multi-select; empty = All) ///
  const handleSelectedCustomers = (optionId) => {
    setSelectedCustomers((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedCustomers = () => {
    setSelectedCustomers([]);
  };

  /// Sites dropdown ///
  const handleSelectedSites = (optionId) => {
    setSelectedSites((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedSites = () => {
    setSelectedSites([]);
  };

  /// Activity Types dropdown ///
  const handleSelectedActivityTypes = (optionId) => {
    setSelectedActivityTypes((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedActivityTypes = () => {
    setSelectedActivityTypes([]);
  };

  /// Users dropdown ///
  const handleSelectedUsers = (optionId) => {
    setSelectedUsers((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedUsers = () => {
    setSelectedUsers([]);
  };

  /// Date picker ///
  const handleSelectedDateRange = (newValue) => {
    if (
      reportingFrequency[0] === "weekly" &&
      newValue.startDate &&
      newValue.endDate
    ) {
      // Automatically set end date 6 days after start (or 7, if desired)
      const start = new Date(newValue.startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      newValue.endDate = end.toISOString().split("T")[0];
    } else if (reportingFrequency[0] === "monthly" && newValue.startDate) {
      // Snap a picked day to its full calendar month.
      const start = new Date(newValue.startDate);
      const y = start.getUTCFullYear();
      const m = start.getUTCMonth();
      newValue.startDate = new Date(Date.UTC(y, m, 1))
        .toISOString()
        .split("T")[0];
      newValue.endDate = new Date(Date.UTC(y, m + 1, 0))
        .toISOString()
        .split("T")[0];
    }
    setDateRange(newValue);
  };

  useEffect(() => {
    // Update the selected sites when selected customers change
    setSelectedSites((currentSelectedSites) =>
      currentSelectedSites.filter((site) =>
        sitesOptions.some((option) => option.id === site),
      ),
    );
  }, [selectedCustomers, sitesOptions]);

  return (
    <div className="flex flex-col h-full overflow-x-visible">
      {/* Scrollable filter content */}
      <div className="flex-grow overflow-visible px-6 pt-12">
        <div className="mb-6">
          <ReportTypeFilter
            singleSelect={true}
            selected={reportType}
            onUpdate={handleReportTypeChange}
            fullWidth={true}
            labelPosition="outset"
          />
        </div>
        {["activity", "shift"].includes(reportType[0]) && (
          <div className="mb-4">
            <ActivityFrequencyFilter
              singleSelect={true}
              selected={reportingFrequency}
              onUpdate={handleFrequencyChange}
              fullWidth={true}
              labelPosition="outset"
              label={
                reportType[0] === "shift"
                  ? "Shift frequency"
                  : "Activity frequency"
              }
            />
          </div>
        )}
        <div className="mb-4">
          <ReportingCustomerFilter
            selected={selectedCustomers}
            onUpdate={handleSelectedCustomers}
            onClear={clearSelectedCustomers}
            fullWidth={true}
            labelPosition="outset"
          />
        </div>
        <div className="mb-4">
          <ReportingSiteFilter
            selected={selectedSites}
            onUpdate={handleSelectedSites}
            onClear={clearSelectedSites}
            customerIds={selectedCustomers}
            setSitesOptions={setSitesOptions}
            fullWidth={true}
            labelPosition="outset"
          />
        </div>
        <div className="mb-4">
          <ReportingUserFilter
            selected={selectedUsers}
            onUpdate={handleSelectedUsers}
            onClear={clearSelectedUsers}
            fullWidth={true}
            labelPosition={"outset"}
          />
        </div>
        <div className="mb-4">
          <ReportingActivityTypeFilter
            selected={selectedActivityTypes}
            onUpdate={handleSelectedActivityTypes}
            onClear={clearSelectedActivityTypes}
            fullWidth={true}
            labelPosition="outset"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
            Date
          </label>
          <Datepicker
            key={dateRange.startDate + "-" + dateRange.endDate}
            startFrom={new Date("2024-01-05")}
            showShortcuts={false}
            inputClassName="h-9 w-64 inline-flex rounded-md px-3 -ml-1 mr-1 items-center text-sm leading-none tracking-normal font-normal text-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800 border-0 pr-10 focus:outline-none"
            separator={"to"}
            value={dateRange}
            onChange={handleSelectedDateRange}
            useRange={reportingFrequency[0] === "weekly"}
            asSingle={reportingFrequency[0] !== "weekly"}
            popoverDirection="up"
          />
        </div>
      </div>

      {/* Sticky footer for Date + Run to avoid popover clipping */}
      <div className="mt-auto dark:border-gray-700 px-5 py-6">
        <Button fullWidth onClick={handleRunClick}>
          Run
        </Button>
      </div>
    </div>
  );
}

export { FilterPlanel };
